import * as React from "react"

import { GLIDE_FRICTION, GLIDE_STOP, releaseVelocity, TRAIL_LENGTH, type TrailPoint } from "./glide"

/** A pointer must move this far before a press becomes a drag. */
export const DRAG_THRESHOLD = 4

/**
 * Presses that are never the start of a pan: a link (the browser drags those
 * itself), somewhere to type, a dialog laid
 * over the page, a layer with its own pointer gestures (drawing a box), or
 * anything that opts out with `data-pan-ignore`.
 */
const IGNORE =
  "a[href], input, textarea, select, [contenteditable=''], [contenteditable='true'], [role=dialog], [role=application], [data-pan-ignore]"

export interface DragPanOptions {
  /**
   * A primary press has landed. Return false to leave it alone (nothing to
   * pan to, say); otherwise note where the view stands, since every move is
   * reported from here.
   */
  onPress: (event: React.PointerEvent<HTMLElement>) => boolean
  /** The drag's offset from the press, in CSS pixels. Applied at most once a frame. */
  onMove: (dx: number, dy: number) => void
  /**
   * One frame of a released drag's glide, moving by (`vx`, `vy`) in the
   * pointer's direction. Returns which axes moved: one that did not has hit an
   * edge, and its glide stops rather than pressing into it. Omit for no glide.
   */
  onGlide?: (vx: number, vy: number) => { x: boolean; y: boolean }
}

export interface DragPanHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void
  onClickCapture: (event: React.MouseEvent<HTMLElement>) => void
}

export interface DragPan {
  /** A drag is under way: the caller drops its transitions and shows a grabbing cursor. */
  dragging: boolean
  /** A released drag is still gliding to a stop. */
  gliding: boolean
  /** Stop a glide, for a zoom or a reframe that takes over the view. */
  stopGlide: () => void
  /** Spread onto the element the page is dragged within. */
  handlers: DragPanHandlers
}

interface Drag {
  pointerId: number
  x: number
  y: number
  moved: boolean
}

/**
 * Drag the page to move around it, and let it glide to a stop when released,
 * as touch and PDF viewers do. What "moving" means is the caller's: a scroll
 * position, or the offset of a transformed stage.
 *
 * A press only becomes a drag once the pointer travels `DRAG_THRESHOLD`, so a
 * plain click still lands on a finding's box, and the click that ends a real
 * drag is swallowed rather than selecting whatever the drag began on. Moves
 * are applied once a frame, so a fast mouse does not queue more work than the
 * screen can show.
 */
export function useDragPan(options: DragPanOptions): DragPan {
  const optionsRef = React.useRef(options)
  React.useLayoutEffect(() => {
    optionsRef.current = options
  })

  const [dragging, setDragging] = React.useState(false)
  const [gliding, setGliding] = React.useState(false)
  const dragRef = React.useRef<Drag | null>(null)
  const trailRef = React.useRef<TrailPoint[]>([])
  const pendingRef = React.useRef<{ dx: number; dy: number } | null>(null)
  const moveFrameRef = React.useRef(0)
  const glideFrameRef = React.useRef(0)
  // A drag ends in a click on whatever it started on; that click is not a selection.
  const swallowClickRef = React.useRef(false)

  React.useEffect(
    () => () => {
      cancelAnimationFrame(moveFrameRef.current)
      cancelAnimationFrame(glideFrameRef.current)
    },
    [],
  )

  const stopGlide = React.useCallback((): void => {
    if (!glideFrameRef.current) return
    cancelAnimationFrame(glideFrameRef.current)
    glideFrameRef.current = 0
    setGliding(false)
  }, [])

  const flush = (): void => {
    cancelAnimationFrame(moveFrameRef.current)
    moveFrameRef.current = 0
    const pending = pendingRef.current
    pendingRef.current = null
    if (pending) optionsRef.current.onMove(pending.dx, pending.dy)
  }

  const glide = ({ vx, vy }: { vx: number; vy: number }): void => {
    if (!optionsRef.current.onGlide) return
    if (Math.abs(vx) < GLIDE_STOP && Math.abs(vy) < GLIDE_STOP) return
    let speed = { vx, vy }
    setGliding(true)
    const step = (): void => {
      const moved = optionsRef.current.onGlide?.(speed.vx, speed.vy) ?? { x: false, y: false }
      speed = {
        vx: moved.x ? speed.vx * GLIDE_FRICTION : 0,
        vy: moved.y ? speed.vy * GLIDE_FRICTION : 0,
      }
      if (Math.abs(speed.vx) > GLIDE_STOP || Math.abs(speed.vy) > GLIDE_STOP) {
        glideFrameRef.current = requestAnimationFrame(step)
      } else {
        glideFrameRef.current = 0
        setGliding(false)
      }
    }
    glideFrameRef.current = requestAnimationFrame(step)
  }

  const onPointerDown = (event: React.PointerEvent<HTMLElement>): void => {
    // A new press starts clean: a swallow left over from a drag whose click
    // never came (a cancelled pointer) must not eat this one.
    swallowClickRef.current = false
    if (event.button !== 0 || dragRef.current) return
    if (event.target instanceof Element && event.target.closest(IGNORE)) return
    stopGlide()
    if (!optionsRef.current.onPress(event)) return
    trailRef.current = [{ x: event.clientX, y: event.clientY, t: performance.now() }]
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      moved: false,
    }
  }

  const onPointerMove = (event: React.PointerEvent<HTMLElement>): void => {
    const drag = dragRef.current
    if (drag?.pointerId !== event.pointerId) return
    const dx = event.clientX - drag.x
    const dy = event.clientY - drag.y
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      drag.moved = true
      setDragging(true)
      // Captured only once it is a drag, so a plain press still clicks a box.
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    trailRef.current.push({ x: event.clientX, y: event.clientY, t: performance.now() })
    if (trailRef.current.length > TRAIL_LENGTH) trailRef.current.shift()
    pendingRef.current = { dx, dy }
    if (!moveFrameRef.current) {
      moveFrameRef.current = requestAnimationFrame(flush)
    }
  }

  const endDrag = (event: React.PointerEvent<HTMLElement>): void => {
    const drag = dragRef.current
    if (drag?.pointerId !== event.pointerId) return
    dragRef.current = null
    if (!drag.moved) return
    setDragging(false)
    flush()
    // Only a release is followed by a click, and only a release is a throw: a
    // cancelled pointer (touch taken over, a context menu) neither clicks nor glides.
    if (event.type !== "pointerup") return
    swallowClickRef.current = true
    glide(releaseVelocity(trailRef.current, performance.now()))
  }

  const onClickCapture = (event: React.MouseEvent<HTMLElement>): void => {
    if (!swallowClickRef.current) return
    swallowClickRef.current = false
    event.stopPropagation()
    event.preventDefault()
  }

  return {
    dragging,
    gliding,
    stopGlide,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
  }
}
